import { MarkdownView } from "../doc/MarkdownView";
import type { CampaignEmail } from "./email-campaign";

// One e-mail, opened in place on the timeline: what the document knows about
// it (when it goes, to whom, what it has to do), the subjects worth testing,
// and the body itself.
//
// The HEAD belongs to the card above (CampaignTimeline): it carries the order,
// the subject and both copy buttons, and it sticks while this body scrolls.
// Nothing here repeats it.

function replace(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    template
  );
}

function CopyButton(props: {
  className: string;
  state: "idle" | "done" | "failed";
  label: string;
  t: (key: string) => string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={props.className} onClick={props.onClick}>
      {props.state === "done"
        ? props.t("sales.emails.copied")
        : props.state === "failed"
          ? props.t("sales.emails.copyFailedShort")
          : props.label}
    </button>
  );
}

export function EmailDetail(props: {
  email: CampaignEmail;
  total: number;
  html: boolean;
  copyState: (key: string) => "idle" | "done" | "failed";
  t: (key: string) => string;
  onCopyAltSubject: (subject: string, key: string) => void;
  onStep: (delta: number) => void;
}) {
  const { email } = props;
  const facts: Array<{ key: string; label: string; value: string; note?: string | null }> = [];
  if (email.day) facts.push({ key: "day", label: props.t("sales.emails.detail.day"), value: email.day });
  if (email.segment) {
    facts.push({
      key: "segment",
      label: props.t("sales.emails.detail.segment"),
      value: email.segment,
      note: email.segmentNote
    });
  }
  if (email.task) facts.push({ key: "task", label: props.t("sales.emails.detail.task"), value: email.task });
  for (const note of email.notes) {
    facts.push({ key: `note-${note.label}-${note.value}`, label: note.label || props.t("sales.emails.detail.note"), value: note.value });
  }

  return (
    <div className="ec-detail">
      {/* A fold with no briefing states nothing about itself; an empty grid
          would be a gap the client reads as a bug. */}
      {facts.length > 0 && (
        <dl className="ec-detail-facts">
          {facts.map((fact) => (
            <div className="ec-detail-fact" key={fact.key}>
              <dt>{fact.label}</dt>
              <dd>
                {fact.value}
                {fact.note && <span className="ec-detail-fact-note">{fact.note}</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {email.altSubjects.length > 0 && (
        <div className="ec-detail-alts">
          <p className="ec-detail-alts-label">{props.t("sales.emails.detail.altSubjects")}</p>
          <ul>
            {email.altSubjects.map((subject, position) => (
              <li key={`${position}-${subject}`}>
                <span>{subject}</span>
                <CopyButton
                  className="quiet-link ec-detail-alt-copy"
                  state={props.copyState(`alt:${email.index}:${position}`)}
                  label={props.t("sales.emails.copySubject")}
                  t={props.t}
                  onClick={() => props.onCopyAltSubject(subject, `alt:${email.index}:${position}`)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="ec-detail-body">
        {/* A fold fragment no longer starts with <doc>, so the renderer is
            told which dialect it is reading. */}
        <MarkdownView md={email.body} docHtml={props.html} nested />
      </div>

      <nav className="ec-detail-nav" aria-label={props.t("sales.emails.detail.navLabel")}>
        <button
          type="button"
          className="quiet-link"
          disabled={email.index <= 1}
          onClick={() => props.onStep(-1)}
        >
          {props.t("sales.emails.detail.previous")}
        </button>
        <small>{replace(props.t("sales.emails.card.position"), { index: email.index, total: props.total })}</small>
        <button
          type="button"
          className="quiet-link"
          disabled={email.index >= props.total}
          onClick={() => props.onStep(1)}
        >
          {props.t("sales.emails.detail.next")}
        </button>
      </nav>
    </div>
  );
}
